import { Copy, FolderOpen, LogOut, Pencil, X } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import {
  useAppStore,
  useCurrentProjectId,
  useDuplicateProject,
  useLoadProject,
  useProjects,
  useRenameProject,
  useSaveCurrentProject,
} from '../store/hooks';

interface SettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsPopup({ isOpen, onClose }: SettingsPopupProps) {
  const { logout, user } = useAuth();
  const projects = useProjects();
  const currentProjectId = useCurrentProjectId();
  const loadProject = useLoadProject();
  const duplicateProject = useDuplicateProject();
  const renameProject = useRenameProject();
  const saveCurrentProject = useSaveCurrentProject();
  const currentProject = currentProjectId ? projects[currentProjectId] : null;

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
      onClose();
    }
  };

  const handleSwitchProject = () => {
    saveCurrentProject();
    useAppStore.setState({ currentProjectId: null });
    onClose();
  };

  const handleSaveAsNew = () => {
    if (!currentProjectId) return;
    const current = projects[currentProjectId];
    const name = prompt('Name for new project', `${current.name} Copy`);
    if (name) {
      const id = duplicateProject(currentProjectId, name);
      loadProject(id);
      onClose();
    }
  };

  const handleRenameCurrent = () => {
    if (!currentProjectId) return;
    const current = projects[currentProjectId];
    const name = prompt('Rename project', current.name);
    if (name) {
      renameProject(currentProjectId, name);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#0c1222] border border-[#1a2d4d] rounded-xl max-w-md w-full mx-4 p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          aria-label="Close settings"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <h2 className="text-xl font-bold text-white mb-6">Settings</h2>

        {/* User Info */}
        {user && (
          <div className="mb-6 pb-6 border-b border-[#1a2d4d]">
            <p className="text-gray-400 text-sm">Signed in as</p>
            <p className="text-white font-medium">{user.email}</p>
          </div>
        )}

        {currentProject?.isLocalOnly && (
          <p className="text-red-500 text-sm mb-4">
            NOTE: This project is local-only due to a sync failure -- ensure you save and backup
            your work as you go
          </p>
        )}

        {/* Actions */}
        <div className="space-y-4">
          {/* Project Management */}
          <div className="space-y-2 pb-4 border-b border-[#1a2d4d]">
            <p className="text-gray-300 text-sm mb-2">Project Actions</p>
            <button
              onClick={handleSwitchProject}
              className={cn(
                'w-full bg-gray-600 hover:bg-gray-700 text-white',
                'px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2'
              )}
            >
              <FolderOpen className="w-4 h-4" /> Switch Project
            </button>
            <button
              onClick={handleSaveAsNew}
              className={cn(
                'w-full bg-gray-600 hover:bg-gray-700 text-white',
                'px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2'
              )}
            >
              <Copy className="w-4 h-4" /> Save As New
            </button>
            <button
              onClick={handleRenameCurrent}
              className={cn(
                'w-full bg-gray-600 hover:bg-gray-700 text-white',
                'px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2'
              )}
            >
              <Pencil className="w-4 h-4" /> Rename Current
            </button>
          </div>
          {/* Logout Button */}
          <div>
            <button
              onClick={handleLogout}
              className={cn(
                'w-full bg-gray-600 hover:bg-gray-700 text-white',
                'px-4 py-3 rounded-lg transition-colors',
                'font-medium flex items-center justify-center gap-2'
              )}
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
